import React, { Fragment, ReactElement, ReactNode, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
    ExclamationTriangleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import Form, { FormProps, SubmissionButtons } from "@/Components/Form/Form";
import { ModalContext } from "@/Components/ModalContext.ts";

export type ModalVariantProps = "primary" | "success" | "warning" | "danger";

type ModalProps = {
    variant?: ModalVariantProps;
    // eslint-disable-next-line
    Icon?: typeof ExclamationTriangleIcon;
    show?: boolean;
    onClose: () => void;
    title: string | ReactNode;
    children: string | ReactNode;
    buttons?: ReactNode;
    formModal?: boolean;
    fullScreen?: boolean;
};

const BlankWrapper = (props?: FormProps<never>) => {
    return <>{props.children}</>;
};

const Modal: React.FC<ModalProps> = ({
    variant,
    Icon = ExclamationTriangleIcon,
    show,
    onClose,
    fullScreen,
    ...props
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    let variantBg, variantText, variantFocus;

    /**
     * Start of special code for Form Modals
     */
    let PanelWrapper = BlankWrapper;
    let panelWrapperProps: FormProps<never> = null;
    let children = props.children;
    let buttons = props.buttons;

    const childrenArray = React.Children.toArray(props.children);
    if (childrenArray.length === 1 && typeof childrenArray[0] === "object") {
        const component = childrenArray[0];
        if (
            "props" in component &&
            typeof component.type === "function" &&
            (component.type.name === "Form" ||
                component.type.name === Form.name)
        ) {
            const form = component as ReactElement<
                FormProps<never>,
                typeof Form
            >;
            const { children: formChildren, ...newProps } = form.props;
            panelWrapperProps = {
                hideDefaultButtons: true,
                hideErrors: true,
                ...newProps,
            };
            PanelWrapper = Form;
            children = formChildren;
            buttons = <SubmissionButtons />;
        }
    }
    /**
     * End of special code for Form Modals
     */

    switch (variant) {
        case "success":
            variantBg = "bg-green-100";
            variantText = "text-green-600";
            variantFocus = "focus:ring-green-500";
            break;
        case "warning":
            variantBg = "bg-yellow-100";
            variantText = "text-yellow-600";
            variantFocus = "focus:ring-yellow-500";
            break;
        case "danger":
            variantBg = "bg-red-100";
            variantText = "text-red-600";
            variantFocus = "focus:ring-red-500";
            break;
        default:
            variantBg = "bg-indigo-100";
            variantText = "text-indigo-600";
            variantFocus = "focus:ring-indigo-500";
            break;
    }

    const fs = fullScreen ? "" : "sm:max-w-lg sm:my-8 sm:p-6";

    return (
        <ModalContext value={{ modalRef: modalRef }}>
            <Transition.Root show={show} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div
                            className={`flex min-h-full items-end justify-center p-4 text-center sm:items-center ${fullScreen ? "m-0 p-0" : "sm:p-0"}`}
                        >
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                {/* Used to contain overflow-hidden */}
                                <Dialog.Panel
                                    className={`relative w-full transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all ${fs}`}
                                    ref={modalRef}
                                >
                                    <PanelWrapper {...panelWrapperProps}>
                                        <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                            <button
                                                type="button"
                                                className={`rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-offset-2 focus:outline-hidden ${variantFocus}`}
                                                onClick={onClose}
                                            >
                                                <span className="sr-only">
                                                    Close
                                                </span>
                                                <XMarkIcon
                                                    className="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                        <div className="sm:flex sm:items-start">
                                            <div
                                                className={`mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${variantBg} sm:mx-0 sm:h-10 sm:w-10`}
                                            >
                                                {/*<ExclamationIcon className={`h-6 w-6 ${variantText}`} aria-hidden="true"/>*/}
                                                <Icon
                                                    className={`h-6 w-6 ${variantText}`}
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div className="mt-3 grow text-center sm:mt-0 sm:ml-4 sm:text-left">
                                                <Dialog.Title
                                                    as="h3"
                                                    className="text-lg leading-6 font-medium text-gray-900"
                                                >
                                                    {props.title}
                                                </Dialog.Title>
                                                <div className="mt-2 text-sm text-gray-500">
                                                    {children}
                                                </div>
                                            </div>
                                        </div>
                                        {buttons && (
                                            <div className="mt-5 flex flex-row-reverse justify-between gap-4 sm:mt-4 sm:justify-start">
                                                {buttons}
                                            </div>
                                        )}
                                    </PanelWrapper>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </ModalContext>
    );
};

export default Modal;
